// Tiny hash-based router. Routes:
//   #/              -> overview
//   #/r/:id         -> per-river detail (drawer overlay)
//   #/blog          -> devotional listing
//   #/blog/:slug    -> devotional entry
//   #/privacy       -> privacy policy
//   #/cookies       -> cookies & storage
//
// The lens + region filters are orthogonal and live in localStorage —
// toggling them doesn't change route.

export type Route =
  | { name: 'overview' }
  | { name: 'river'; id: string }
  | { name: 'blog' }
  | { name: 'blog-entry'; slug: string }
  | { name: 'privacy' }
  | { name: 'cookies' };

type Listener = (route: Route) => void;
const listeners = new Set<Listener>();

function parse(hash: string): Route {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  const clean = h.startsWith('/') ? h.slice(1) : h;
  const parts = clean.split('/').filter(Boolean);
  if (parts[0] === 'r' && parts[1]) return { name: 'river', id: parts[1] };
  if (parts[0] === 'blog' && parts[1]) return { name: 'blog-entry', slug: parts[1] };
  if (parts[0] === 'blog') return { name: 'blog' };
  if (parts[0] === 'privacy') return { name: 'privacy' };
  if (parts[0] === 'cookies') return { name: 'cookies' };
  return { name: 'overview' };
}

export function currentRoute(): Route {
  return parse(window.location.hash);
}

export function navigate(route: Route): void {
  let hash: string;
  switch (route.name) {
    case 'river': hash = `#/r/${route.id}`; break;
    case 'blog': hash = '#/blog'; break;
    case 'blog-entry': hash = `#/blog/${route.slug}`; break;
    case 'privacy': hash = '#/privacy'; break;
    case 'cookies': hash = '#/cookies'; break;
    default: hash = '#/';
  }
  if (window.location.hash === hash) {
    emit();
  } else {
    window.location.hash = hash;
  }
}

export function onRouteChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(): void {
  const r = currentRoute();
  for (const l of listeners) l(r);
}

window.addEventListener('hashchange', emit);
